import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { ShipCard } from "../../../../Cards/ShipCard";
import { FormLabel } from "../../../FormLabel";
import { Input } from "../../../Input";
import { Select } from "../../../Select";
import { Toggle } from "../../../Toggle";
import { Checkbox } from "../../../Checkbox";
import { Button } from "../../../../Buttons/Button";
import { LoadingLayout } from "../../../../Layouts/LoadingLayout";
import {
  useGetSubdivisions,
  useGetUserAddresses,
} from "../../../../../api/hooks";
import { useShipContext } from "../../../../../context/shipContext";
import { useAuthContext } from "../../../../../context/authContext";
import {
  COMPLETE_SENDER_DETAILS_STEP,
  SET_AUTOCOMPLETE_ENABLED_FROM,
} from "../../../../../reducers/types";
import {
  getExistingAddressOptions,
  mapSubdivisionsToSelectOptions,
} from "../../../../../helpers";
import { fromCountries } from "../../../../../data/countries";
import { AutocompleteToggle } from "../../../AutocompleteToggle";
import { AutocompleteInfoBanner } from "../../../../Alerts/AutocompleteInfoBanner";
import { yupResolver } from "@hookform/resolvers/yup";
import { shipFromSchema } from "../../../validations/ship";
import { AddressAutocomplete } from "../../../AddressAutocomplete";

const isEmptyObject = (obj) => !obj || Object.keys(obj).length === 0;

export function ShipFromForm() {
  const { state: shipState, dispatch } = useShipContext();
  const autocompleteEnabled = shipState.from.autocompleteEnabled;

  const { state: authState } = useAuthContext();
  const email = authState.me?.email;
  const name = authState.me?.name;

  const toggleAutocomplete = () => {
    dispatch({
      type: SET_AUTOCOMPLETE_ENABLED_FROM,
      payload: !autocompleteEnabled,
    });
  };

  // Track if the user has manually interacted with shouldSaveAddress
  const userSetSavePreference = useRef(false);

  // Determine if user has previously submitted this step (came back via "Previous" button)
  const hasSubmittedBefore = shipState.from.isExistingAddress !== undefined;

  const { control, watch, handleSubmit, setValue, 
    // formState: { errors } 
  } = useForm({
    defaultValues: {
      isExistingAddress: shipState.from.isExistingAddress ?? undefined,
      existingAddress: shipState.from.existingAddress ?? {},
      shouldSaveAddress: shipState.from.shouldSaveAddress ?? false,
      country: shipState.from?.country?.id
        ? shipState.from.country
        : fromCountries[0],
      email: shipState.from?.email !== "" ? shipState.from.email : email,
      name: shipState.from?.name !== "" ? shipState.from.name : name,
      address1: shipState.from?.address1 ?? "",
      address2: shipState.from?.address2 ?? "",
      city: shipState.from?.city ?? "",
      state: shipState.from?.state ?? {},
      zip: shipState.from?.zip ?? "",
      phone: shipState.from?.phone ?? "",
    },
    resolver: yupResolver(shipFromSchema),
  });

//   useEffect(() => {
//   console.log("Form errors:", errors);
// }, [errors]);

  const isExistingAddress = watch("isExistingAddress");
  const existingAddressValue = watch("existingAddress");

  const {
    data: addressData,
    isLoading: isGetUserAddressLoading,
    isError: isGetUserAddressError,
  } = useGetUserAddresses();

  const { data: provinces, isLoading: isProvincesLoading } = useGetSubdivisions(
    "CA",
    !isExistingAddress
  );

  const provinceOptions = mapSubdivisionsToSelectOptions(provinces);

  const existingAddresses = useMemo(
    () => getExistingAddressOptions(addressData?.addresses || []) || [],
    [addressData]
  );

  const hasReachedAddressLimit = existingAddresses.length >= 10;

  const shouldShowExistingAddressToggle =
    !isGetUserAddressError && existingAddresses.length > 0;
  const shouldShowSaveCheckbox = !isExistingAddress && !hasReachedAddressLimit;

  // Effect 1: Auto-select first existing address for FRESH forms only
  useEffect(() => {
    // Skip if user has already submitted this step before (returning user)
    if (hasSubmittedBefore) return;
    // Skip if no addresses available
    if (!existingAddresses.length) return;

    setValue("isExistingAddress", true, { shouldDirty: false });
    setValue("existingAddress", existingAddresses[0], { shouldDirty: false });
  }, [existingAddresses, hasSubmittedBefore, setValue]);

  // Effect 2: Guard against empty existingAddress when toggle is ON
  // Uses FORM state (existingAddressValue), not context state
  useEffect(() => {
    if (
      isExistingAddress &&
      isEmptyObject(existingAddressValue) &&
      existingAddresses.length
    ) {
      setValue("existingAddress", existingAddresses[0], { shouldDirty: false });
    }
  }, [isExistingAddress, existingAddressValue, existingAddresses, setValue]);

  // Effect 3: Manage shouldSaveAddress checkbox
  useEffect(() => {
    // Force false if at address limit or using existing address
    if (hasReachedAddressLimit) {
      setValue("shouldSaveAddress", false, { shouldDirty: false });
      return;
    }

    if (isExistingAddress) {
      return; // just hide/disable in UI, don't mutate
    }

    // For fresh forms entering manual address, default to true
    // But only if user hasn't explicitly set a preference
    if (!hasSubmittedBefore && !userSetSavePreference.current) {
      setValue("shouldSaveAddress", true, { shouldDirty: false });
    }
  }, [isExistingAddress, hasReachedAddressLimit, hasSubmittedBefore, setValue]);

  useEffect(() => {
    if (!isExistingAddress) return;
    if (!shipState.from.existingAddress?.addressId) return;
    if (!existingAddresses.length) return;

    const match = existingAddresses.find(
      (opt) =>
        opt.address.addressId === shipState.from.existingAddress.addressId
    );

    if (match) {
      setValue("existingAddress", match, { shouldDirty: false });
    }
  }, [
    existingAddresses,
    isExistingAddress,
    shipState.from.existingAddress,
    setValue,
  ]);

  const onSubmit = async (data) => {
    dispatch({ type: COMPLETE_SENDER_DETAILS_STEP, payload: data });
  };

  if (isGetUserAddressLoading || isProvincesLoading) return <LoadingLayout />;

  return (
    <div data-testid="ship-from-step">
      <ShipCard>
        {autocompleteEnabled && !isExistingAddress && (
          <div className="mb-6">
            <AutocompleteInfoBanner />
          </div>
        )}

        <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-12">
          <div className="sm:col-span-12 flex items-center">
            <div className="sm:col-span-3 mr-10">
              <FormLabel title={"Ship From"} />
            </div>

            {shouldShowExistingAddressToggle && (
              <div className="sm:col-span-3">
                <Toggle
                  control={control}
                  name="isExistingAddress"
                  leftLabel="Existing Address?"
                />
              </div>
            )}

            {shouldShowSaveCheckbox && (
              <div className="sm:col-span-3 ml-10">
                <Checkbox
                  label="Save address for future use?"
                  name="shouldSaveAddress"
                  control={control}
                  onChange={() => {
                    userSetSavePreference.current = true;
                  }}
                />
              </div>
            )}
          </div>

          {isExistingAddress ? (
            <div className="sm:col-span-12">
              <Select
                name="existingAddress"
                control={control}
                options={existingAddresses}
              />
            </div>
          ) : (
            <>
              <div className="sm:col-span-9">
                {autocompleteEnabled ? (
                  <>
                    <AddressAutocomplete
                      name="address1"
                      label="Address"
                      control={control}
                      addOnRight={
                        <AutocompleteToggle
                          enabled={autocompleteEnabled}
                          onToggle={toggleAutocomplete}
                          tooltipMessage="Uses Google Address Autocomplete. Supported for: Canada. For other countries, please enter the address manually."
                        />
                      }
                      countryRestriction={["CA"]}
                      onSelectAddress={(mappedAddress) => {
                        setValue("address1", mappedAddress.address1, {
                          shouldDirty: true,
                        });
                        setValue("address2", mappedAddress.address2, {
                          shouldDirty: true,
                        });
                        setValue("city", mappedAddress.city, {
                          shouldDirty: true,
                        });
                        setValue("state", mappedAddress.state, {
                          shouldDirty: true,
                        });
                        setValue("zip", mappedAddress.zip, {
                          shouldDirty: true,
                        });
                        setValue("country", mappedAddress.country, {
                          shouldDirty: true,
                        });
                      }}
                    />
                  </>
                ) : (
                  <>
                    {" "}
                    <Input
                      name="address1"
                      label="Address"
                      control={control}
                      addOnRight={
                        <AutocompleteToggle
                          tooltipMessage="Uses Google Address Autocomplete. Supported for: Canada. For other countries, please enter the address manually."
                          enabled={autocompleteEnabled}
                          onToggle={toggleAutocomplete}
                        />
                      }
                    />
                  </>
                )}
              </div>
              <div className="sm:col-span-3">
                <Input
                  name="address2"
                  label="Apt / Unit / Suite"
                  control={control}
                  helper="Optional"
                />
              </div>
              <div className="sm:col-span-3">
                <Select
                  name="country"
                  label="Country"
                  control={control}
                  options={fromCountries}
                  disabled
                />
              </div>
              <div className="sm:col-span-3">
                <Input name="zip" label="Postal Code" control={control} />
              </div>
              <div className="sm:col-span-3">
                <Select
                  name="state"
                  label="Province"
                  control={control}
                  options={provinceOptions || []}
                  placeholder="Select province"
                />
              </div>
              <div className="sm:col-span-3">
                <Input name="city" label="City" control={control} />
              </div>
              <div className="sm:col-span-4">
                <Input name="name" label="Sender Name" control={control} />
              </div>
              <div className="sm:col-span-4">
                <Input name="phone" label="Contact No." control={control} />
              </div>
              <div className="sm:col-span-4">
                <Input name="email" label="Email Address" control={control} />
              </div>
            </>
          )}
        </div>
      </ShipCard>

      <div className="mt-5 w-full flex justify-end">
        <Button
          text="Continue"
          onClick={handleSubmit(onSubmit)}
          data-testid="sender-continue"
        />
      </div>
    </div>
  );
}
